<script setup lang="ts">
import {
  CheckIcon,
  InfoCircleIcon,
  PlusIcon,
  Trash01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIForm,
  UIFormItem,
  UIInput,
  UIInputGroup,
  UISelect,
  UITag,
  UITooltip
} from '@gohighlevel/ghl-ui'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DurationOption } from '../../models/calendar'
import { setErrorInDurationPicker } from '../../states/app'
import { getFormDurationInMinutes, getDurationOptionsValidationMessage, getInvalidDurationOptionIndices, canAddDurationOption } from '../../utils/durationOptionsValidation'
import { getTooltip } from '../../utils/generalFunctions'

const MAX_DURATION_OPTIONS = 3

const { t } = useI18n()

const props = defineProps<{
  label: string
  tooltip: string
  durationOptions: DurationOption[]
  disabled?: boolean
  currentDuration?: string
  currentDurationUnit?: string
}>()

const emit = defineEmits<{
  (e: 'update:durationOptions', value: DurationOption[]): void
}>()

const formRef = ref()
const formData = reactive({
  durationOptions: [] as DurationOption[],
})

const timeUnits = [
  { label: t('calendar_advanced.availability.mins'), value: 'mins' },
  { label: t('calendar_advanced.availability.hours'), value: 'hours' },
]

const canAddMore = computed(() => props.durationOptions.length < MAX_DURATION_OPTIONS)

const canAddAnotherDuration = computed(() =>
  canAddDurationOption(
    props.durationOptions,
    MAX_DURATION_OPTIONS,
    props.currentDuration,
    props.currentDurationUnit
  )
)

const showMultiDurationActions = computed(
  () => props.durationOptions.length >= 2
)

const summaryValidationMessage = computed(() =>
  getDurationOptionsValidationMessage(props.durationOptions, t)
)

const rules = computed(() => {
  const fieldRules: Record<string, object> = {}

  props.durationOptions.forEach((_, index) => {
    fieldRules[`durationOptions.${index}.duration`] = {
      validator() {
        const invalidIndices = getInvalidDurationOptionIndices(
          props.durationOptions
        )
        return !invalidIndices.includes(index)
      },
      trigger: ['input', 'change'],
    }
  })

  return fieldRules
})

watch(summaryValidationMessage, (message) => {
  setErrorInDurationPicker('slotDuration', Boolean(message))
}, { immediate: true })

watch(
  () => props.durationOptions,
  (newOptions) => {
    formData.durationOptions = newOptions
  },
  { deep: true, immediate: true }
)

function updateOptions(newOptions: DurationOption[]) {
  emit('update:durationOptions', newOptions)
  void nextTick(() => {
    formRef.value?.getForm()?.validate()
  })
}

function getNextDuration(prevDuration: number, prevUnit: string): { duration: number; durationUnit: string } {
  const prevInMins = getFormDurationInMinutes(prevDuration, prevUnit)
  return { duration: prevInMins + 15, durationUnit: 'mins' }
}

function addDuration() {
  if (!canAddMore.value || !canAddAnotherDuration.value) {
    void nextTick(() => {
      formRef.value?.getForm()?.validate()
    })
    return
  }
  const isFirst = props.durationOptions.length === 0

  if (isFirst) {
    const existingDuration = parseInt(props.currentDuration || '0') || 0
    const existingUnit = props.currentDurationUnit || 'mins'
    const next = getNextDuration(existingDuration, existingUnit)
    const newOptions: DurationOption[] = [
      {
        duration: existingDuration,
        durationUnit: existingUnit,
        isDefault: true,
        order: 1,
      },
      {
        duration: next.duration,
        durationUnit: next.durationUnit,
        isDefault: false,
        order: 2,
      },
    ]
    updateOptions(newOptions)
  } else {
    const lastOpt = props.durationOptions[props.durationOptions.length - 1]
    const next = getNextDuration(lastOpt.duration, lastOpt.durationUnit)
    const newOrder = props.durationOptions.length + 1
    const newOptions = [
      ...props.durationOptions,
      {
        duration: next.duration,
        durationUnit: next.durationUnit,
        isDefault: false,
        order: newOrder,
      },
    ]
    updateOptions(newOptions)
  }
}

function removeDuration(index: number) {
  const removing = props.durationOptions[index]
  const newOptions = props.durationOptions
    .filter((_, i) => i !== index)
    .map((opt, i) => ({ ...opt, order: i + 1 }))

  if (removing.isDefault && newOptions.length > 0) {
    newOptions[0] = { ...newOptions[0], isDefault: true }
  }
  updateOptions(newOptions)
}

function setDefault(index: number) {
  const newOptions = props.durationOptions.map((opt, i) => ({
    ...opt,
    isDefault: i === index,
  }))
  updateOptions(newOptions)
}

function updateDurationValue(index: number, rawValue: string) {
  const value = rawValue.replace(/\D/g, '')
  const numValue = parseInt(value) || 0
  const newOptions = props.durationOptions.map((opt, i) =>
    i === index ? { ...opt, duration: numValue } : opt
  )
  updateOptions(newOptions)
}

function updateDurationUnit(index: number, unit: string) {
  const newOptions = props.durationOptions.map((opt, i) =>
    i === index ? { ...opt, durationUnit: unit } : opt
  )
  updateOptions(newOptions)
}

function getDisplayValue(opt: DurationOption): string {
  if (!opt.duration || opt.duration === 0) return ''
  return String(opt.duration)
}

defineExpose({
  async validate() {
    setErrorInDurationPicker(
      'slotDuration',
      Boolean(summaryValidationMessage.value)
    )

    if (summaryValidationMessage.value) {
      void nextTick(() => {
        formRef.value?.getForm()?.validate()
      })
      return false
    }

    try {
      await formRef.value.getForm().validate()
      return true
    } catch {
      return false
    }
  },
})
</script>

<template>
  <div class="max-w-full overflow-hidden mb-3">
    <UIForm
      id="multi-duration-form"
      ref="formRef"
      :model="formData"
      :rules="rules"
    >
      <UIFormItem
        :label="label"
        class="w-full multi-duration-field"
        :tooltip="tooltip ? getTooltip(tooltip) : null"
        :show-feedback="false"
      >
        <div
          class="duration-options-grid w-full"
          :class="{ 'duration-options-grid--multi': showMultiDurationActions }"
        >
          <template
            v-for="(opt, index) in durationOptions"
            :key="index"
          >
            <UIFormItem
              :path="`durationOptions.${index}.duration`"
              :show-label="false"
              :show-feedback="false"
              class="duration-option-input-item min-w-0"
            >
              <UIInputGroup class="w-full min-w-0" :disabled="disabled">
                <UIInput
                  :id="`duration-option-${index}`"
                  :disabled="disabled"
                  :model-value="getDisplayValue(opt)"
                  class="w-96"
                  placeholder=""
                  @update:model-value="updateDurationValue(index, $event)"
                />
                <UISelect
                  :id="`duration-unit-${index}`"
                  :disabled="disabled"
                  :value="opt.durationUnit"
                  class="w-60"
                  :options="timeUnits"
                  @update:value="updateDurationUnit(index, $event)"
                />
              </UIInputGroup>
            </UIFormItem>

            <template v-if="showMultiDurationActions">
              <UITag
                v-if="opt.isDefault"
                type="primary"
                :closable="false"
                size="medium"
                :round="true"
                :bordered="false"
                class="duration-action-tag flex items-center gap-1"
              >
                <CheckIcon class="h-3.5 w-3.5 text-primary-700" />
                {{ t('calendar_advanced.availability.default_duration') }}
              </UITag>
              <UITag
                v-else
                type="default"
                :closable="false"
                size="medium"
                :round="true"
                :bordered="false"
                class="duration-action-tag flex cursor-pointer items-center gap-1"
                @click="setDefault(index)"
              >
                {{ t('calendar_advanced.availability.set_as_default') }}
              </UITag>

              <UIButton
                :id="`remove-duration-${index}`"
                type="button"
                size="small"
                quaternary
                class="shrink-0 justify-self-start"
                :disabled="disabled"
                @click="removeDuration(index)"
              >
                <template #icon>
                  <Trash01Icon class="h-4 w-4 text-gray-700" />
                </template>
              </UIButton>
            </template>
          </template>
        </div>
      </UIFormItem>

      <div class="multi-duration-footer">
        <p
          v-if="summaryValidationMessage"
          class="duration-summary-error n-form-item-feedback--error"
          role="alert"
        >
          {{ summaryValidationMessage }}
        </p>

        <div
          v-if="canAddMore"
          class="add-duration-action flex items-center gap-2"
        >
          <button
            type="button"
            class="flex items-center gap-1 text-sm font-medium"
            :class="
              disabled || !canAddAnotherDuration
                ? 'cursor-not-allowed text-gray-400'
                : 'text-blue-600 hover:text-blue-700'
            "
            :disabled="disabled || !canAddAnotherDuration"
            @click="addDuration"
          >
            <PlusIcon class="h-4 w-4 cursor-pointer" />
            {{ t('calendar_advanced.availability.add_another_duration') }}
          </button>
          <UITooltip>
            <template #trigger>
              <InfoCircleIcon class="h-4 w-4 text-gray-500" />
            </template>
            <p class="max-w-xs">
              {{ t('calendar_advanced.availability.multi_duration_tooltip') }}
            </p>
          </UITooltip>
        </div>
      </div>
    </UIForm>
  </div>
</template>

<style scoped>
.multi-duration-field :deep(.n-form-item) {
  margin-bottom: 0;
}

.multi-duration-footer {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.duration-summary-error {
  position: static;
  margin: 0;
  padding: 0;
  background: none;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--n-feedback-text-color-error, #d03050);
}

.add-duration-action {
  margin: 0;
}

.duration-options-grid {
  display: grid;
  row-gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr);
}

.duration-options-grid--multi {
  column-gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
}

.duration-action-tag {
  justify-self: center;
}

.duration-option-input-item :deep(.n-form-item-blank) {
  min-height: 0;
}

.duration-option-input-item :deep(.n-form-item) {
  margin-bottom: 0;
}
</style>
